"use client";

import { useState, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useMessageConversation, useSendMessage } from "@/lib/api/hooks";
import { listingApi } from "@/lib/api/listings";
import { useI18n } from "@/i18n";

/**
 * 聊天回复弹窗
 *
 * Props:
 *   open           boolean — 是否显示
 *   onClose        () => void — 关闭回调
 *   otherUserId    number  — 对端用户 ID（询价的买家）
 *   otherName      string  — 对端昵称
 *   otherAvatar    string  — 对端头像 URL（可空）
 *   listingId      number  — 关联车辆/商品 ID（可空）
 *   listingTitle   string  — 车辆标题（用于头部展示）
 *   initialMessage string  — 询价原始消息（头部引用展示）
 */
export default function ChatModal({
    open,
    onClose,
    otherUserId,
    otherName = null,
    otherAvatar = "",
    listingId = null,
    listingTitle = "",
    listingCover = "",
    initialMessage = "",
    position = "center",
}) {
    const { t } = useI18n();
    const [refreshKey, setRefreshKey] = useState(0);
    const [currentUser, setCurrentUser] = useState(null);
    const [input, setInput] = useState("");
    const scrollRef = useRef(null);
    const hasLoadedUser = useRef(false);
    // 标记：首次加载完第一页后需要滚到底部（loadMore 后不要再滚）
    const needScrollToBottomRef = useRef(false);

    // === 顶部{t("account.chatPullLoadMore")}（拉弹簧） ===
    // 状态：idle | pulling（未过阈值） | ready（过阈值可松手） | loading
    const [pullState, setPullState] = useState("idle");
    const [pullDistance, setPullDistance] = useState(0);  // 0 ~ 80px
    const pullStateRef = useRef("idle");
    const touchStartYRef = useRef(null);
    const accumulatedPullRef = useRef(0);  // 累计被拉过的距离（考虑回弹）
    const pullingSinceTopRef = useRef(false);
    // 顶部加载延迟态：滚到顶部先显示 0.5s loading，再真正请求下一页
    const [topLoading, setTopLoading] = useState(false);
    const topLoadingTimerRef = useRef(null);
    const topPendingRef = useRef(false);
    const PULL_THRESHOLD = 55;   // 超过这个距离 → ready
    const MAX_PULL = 80;         // 最大拉伸距离（弹簧阻尼）

    const applyPullTransform = useCallback((dist) => {
        const el = scrollRef.current;
        if (!el) return;
        // 弹簧阻尼：超过阈值后再拉越来越紧
        let damped = dist;
        if (dist > PULL_THRESHOLD) {
            damped = PULL_THRESHOLD + Math.pow(dist - PULL_THRESHOLD, 0.6);
        }
        if (damped > MAX_PULL) damped = MAX_PULL;
        // 给列表加 translateY，让气泡整体往下滑露出拉弹簧区
        el.style.transform = `translateY(${damped}px)`;
        el.style.transition = "none";
    }, []);

    const resetPull = useCallback(() => {
        setPullState("idle");
        setPullDistance(0);
        pullingSinceTopRef.current = false;
        accumulatedPullRef.current = 0;
        const el = scrollRef.current;
        if (el) {
            el.style.transition = "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
            el.style.transform = "translateY(0)";
            setTimeout(() => { el.style.transition = ""; }, 280);
        }
    }, []);

    // 打开弹窗时重置拉弹簧
    useEffect(() => {
        if (open) {
            resetPull();
        }
    }, [open, resetPull]);

    // 打开弹窗时获取当前登录用户信息
    useEffect(() => {
        if (open && !hasLoadedUser.current) {
            hasLoadedUser.current = true;
            listingApi.getAuthInfo().then((res) => {
                const u = res?.data || res;
                setCurrentUser(u);
            }).catch(() => {});
        }
    }, [open]);

    // 每次打开弹窗 → 重置分页 + 标记首次滚底
    useEffect(() => {
        if (open) {
            setRefreshKey((k) => k + 1);
            setInput("");
            needScrollToBottomRef.current = true;
        } else {
            hasLoadedUser.current = false;
        }
    }, [open, otherUserId]);

    // 消息列表（自管理分页）
    const conv = useMessageConversation({
        enabled: open && !!otherUserId,
        otherUserId,
        listingId: listingId ?? undefined,
        pageSize: 20,
        refreshKey,
    });

    // === 以下代码依赖 conv，必须在 conv 声明之后 ===

    // loadMore 完成后：收回弹簧 + 复位顶部 loading 延迟状态
    const lastLoadingMoreRef = useRef(false);
    useEffect(() => {
        if (!conv.loadingMore && lastLoadingMoreRef.current) {
            lastLoadingMoreRef.current = false;
            if (pullStateRef.current === "loading") resetPull();
            // 请求结束：复位顶部 loading 与延迟标记
            setTopLoading(false);
            topPendingRef.current = false;
            if (topLoadingTimerRef.current) {
                clearTimeout(topLoadingTimerRef.current);
                topLoadingTimerRef.current = null;
            }
        }
        lastLoadingMoreRef.current = conv.loadingMore;
    }, [conv.loadingMore, resetPull]);

    // === 指针事件：触摸 + 鼠标统一处理 ===
    const onPointerDown = useCallback((e) => {
        const el = scrollRef.current;
        if (!el) return;
        if (conv.loadingMore) return;
        pullingSinceTopRef.current = el.scrollTop <= 2;
        accumulatedPullRef.current = 0;
        touchStartYRef.current = e.clientY;
    }, [conv.loadingMore]);

    const onPointerMove = useCallback((e) => {
        if (pullStateRef.current === "loading") return;
        const startY = touchStartYRef.current;
        if (startY == null) return;
        const el = scrollRef.current;
        if (!el) return;
        const deltaY = startY - e.clientY;
        if (pullingSinceTopRef.current && deltaY > 0) {
            accumulatedPullRef.current = Math.min(deltaY, MAX_PULL * 1.4);
            const dist = accumulatedPullRef.current;
            setPullDistance(dist);
            applyPullTransform(dist);
            if (dist >= PULL_THRESHOLD) {
                if (pullStateRef.current !== "ready") { pullStateRef.current = "ready"; setPullState("ready"); }
            } else {
                if (pullStateRef.current !== "pulling") { pullStateRef.current = "pulling"; setPullState("pulling"); }
            }
        }
    }, [applyPullTransform]);

    // 发送 mutation
    const sendMut = useSendMessage();

    // 首次加载完第一页 → 定位到底端（滚动条在最下面），禁用 smooth 动画。
    // 用 ResizeObserver 监听内容高度：数据/图片陆续撑高时持续滚到底，避免首帧高度为 0 误判。
    useLayoutEffect(() => {
        if (!open || conv.loading) return;
        if (!needScrollToBottomRef.current) return;
        const el = scrollRef.current;
        if (!el) return;

        const prevBehavior = el.style.scrollBehavior;
        el.style.scrollBehavior = "auto";

        // 单次把滚动条设到最底部
        const scrollToBottom = () => {
            if (!needScrollToBottomRef.current) return;
            // 只有内容确实超过可视高度时才滚，避免内容为空时把滚动条卡在 0
            if (el.scrollHeight > el.clientHeight) {
                el.scrollTop = el.scrollHeight;
            }
        };

        // 初次滚动（等首次绘制帧确保布局完成）
        requestAnimationFrame(() => requestAnimationFrame(scrollToBottom));

        // 高度变化（更多数据/图片加载）时不断滚到底，直到标记被清除
        let ro = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(scrollToBottom);
            ro.observe(el);
        }

        return () => { if (ro) ro.disconnect(); };
    }, [open, conv.loading, conv.list.length]);

    // 发送后滚到底部（同样禁用 smooth scroll，干净利落）
    useEffect(() => {
        if (!sendMut.isSuccess) return;
        const el = scrollRef.current;
        if (!el) return;
        const prevBehavior = el.style.scrollBehavior;
        el.style.scrollBehavior = "auto";
        requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight;
            requestAnimationFrame(() => { el.style.scrollBehavior = prevBehavior; });
        });
    }, [sendMut.isSuccess, conv.list.length]);

    // 通用 loadMore + 位置锚定：调用后保存 scrollHeight，数据到达后补偿 scrollTop
    const requestLoadMoreWithAnchor = useCallback(() => {
        if (!conv.hasMore || conv.loadingMore || conv.loading) return false;
        const el = scrollRef.current;
        if (!el) return false;
        const prevHeight = el.scrollHeight;
        const prevBehavior = el.style.scrollBehavior;
        el.style.scrollBehavior = "auto";
        conv.loadMore();
        const restore = () => {
            requestAnimationFrame(() => {
                const delta = el.scrollHeight - prevHeight;
                el.scrollTop = delta;
                requestAnimationFrame(() => { el.style.scrollBehavior = prevBehavior; });
            });
        };
        const observer = new MutationObserver(restore);
        observer.observe(el, { childList: true, subtree: true });
        setTimeout(() => observer.disconnect(), 2000);
        return true;
    }, [conv.hasMore, conv.loadingMore, conv.loading, conv.loadMore]);

    const onPointerUp = useCallback(() => {
        touchStartYRef.current = null;
        if (pullStateRef.current === "loading") return;
        const dist = accumulatedPullRef.current;
        if (dist >= PULL_THRESHOLD && conv.hasMore && !conv.loadingMore && !conv.loading) {
            pullStateRef.current = "loading";
            setPullState("loading");
            setPullDistance(PULL_THRESHOLD);
            applyPullTransform(PULL_THRESHOLD);
            requestLoadMoreWithAnchor();
        } else {
            resetPull();
        }
    }, [conv.hasMore, conv.loadingMore, conv.loading, applyPullTransform, requestLoadMoreWithAnchor, resetPull]);
    // 鼠标滚轮/触屏滑动时整屏向上滚到列表头部 → 先显示 0.5s loading，再请求更早消息
    // 严格条件：scrollTop 必须 ≤ 2（真正到顶），且距底部至少 100px（确保不在底部）
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const scrollTop = el.scrollTop;
        const scrollHeight = el.scrollHeight;
        const clientHeight = el.clientHeight;
        const distanceToBottom = scrollHeight - scrollTop - clientHeight;

        // 用户主动滚离底部（超过 40px）→ 清除首次滚底标记，停止被拉回底部
        if (needScrollToBottomRef.current && distanceToBottom > 40) {
            needScrollToBottomRef.current = false;
        }

        // 到顶 + 距底足够远 + 还有更多 + 当前未在加载/未在延迟中
        if (scrollTop <= 2 && distanceToBottom > 100 &&
            conv.hasMore && !conv.loadingMore && !conv.loading && !topPendingRef.current) {

            // 立即显示顶部 loading 指示器
            setTopLoading(true);

            // 记录延迟中状态，防止触发期间被反复滚动重复触发
            topPendingRef.current = true;

            // 清除旧的延时定时器
            if (topLoadingTimerRef.current) {
                clearTimeout(topLoadingTimerRef.current);
                topLoadingTimerRef.current = null;
            }

            // 0.5s 后再真正发起请求
            topLoadingTimerRef.current = setTimeout(() => {
                topLoadingTimerRef.current = null;
                requestLoadMoreWithAnchor();
            }, 500);
        }
    }, [conv.hasMore, conv.loadingMore, conv.loading, requestLoadMoreWithAnchor]);

    const handleSend = useCallback(async () => {
        const text = input.trim();
        if (!text || sendMut.isPending) return;
        try {
            await sendMut.mutateAsync({
                receiverId: otherUserId,
                listingId: listingId ?? undefined,
                content: text,
            });
            setInput("");
        } catch (err) {
            console.error("[chat] send failed:", err);
            // 失败提示
            if (typeof document !== "undefined") {
                const toast = document.createElement("div");
                toast.style.cssText = "position:fixed;top:16px;left:50%;transform:translateX(-50%);z-index:99999;background:#EF4444;color:#fff;padding:10px 24px;border-radius:8px;font-size:14px;font-weight:500;box-shadow:0 4px 12px rgba(0,0,0,0.15);";
                toast.textContent = err?.message || t("account.chatSendFail");
                document.body.appendChild(toast);
                setTimeout(() => toast.remove(), 2500);
            }
        }
    }, [input, sendMut, otherUserId, listingId]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    // ESC 关闭
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // 关闭弹窗时清理顶部加载延迟定时器并复位状态
    useEffect(() => {
        if (open) return;
        if (topLoadingTimerRef.current) {
            clearTimeout(topLoadingTimerRef.current);
            topLoadingTimerRef.current = null;
        }
        topPendingRef.current = false;
        setTopLoading(false);
    }, [open]);

    if (!open || typeof document === "undefined") return null;

    // 默认头像
    const DEFAULT_AVATAR = "https://iyoubucket.oss-cn-hangzhou.aliyuncs.com/guineemall/home/default-avatar.png";

    // 方向判断
    const myId = currentUser?.id;
    const isMine = (msg) => myId != null && Number(msg?.senderId) === Number(myId);

    // 时间格式化
    const fmtTime = (t) => {
        if (!t) return "";
        const d = new Date(t);
        if (isNaN(d.getTime())) return String(t);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    // 转义还原
    const unescape = (s) => {
        if (!s) return "";
        return String(s)
            .replace(/\\"/g, '"')
            .replace(/\\'/g, "'")
            .replace(/\\\\/g, "\\")
            .replace(/\\n/g, "\n")
            .replace(/\\t/g, "\t")
            .replace(/\\r/g, "");
    };

    const aboveBtn = position === "aboveBtn";

    return createPortal(
        <div
            className="chat-modal-root"
            onClick={onClose}
            style={aboveBtn ? {
                justifyContent: "flex-end",
                alignItems: "flex-end",
                padding: "104px 24px 96px",
            } : undefined}
        >
            <div
                className="chat-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                style={aboveBtn ? { height: "min(620px, calc(100vh - 200px))", marginBottom: 0 } : undefined}
            >
                {/* 头部 */}
                <div className="chat-modal-header">
                    <div className="chat-modal-avatar">
                        {listingCover ? (
                            <img src={listingCover} alt={listingTitle || `${t("account.chatListingLabel").replace("{id}", listingId ?? "")}`} />
                        ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="18" height="18">
                                <path d="M19 17H5l1.5-6h11L19 17zM6 17v2h3m9-2v2h-3M7 11V7h10v4" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                    </div>
                    <div className="chat-modal-meta">
                        <div className="chat-modal-name">{listingTitle || `${t("account.chatListingLabelNone").replace("{id}", listingId ?? "")}`}</div>
                        {(otherName || otherUserId) && (
                            <div className="chat-modal-listing-text">
                                <strong>{otherName || t("account.chatSellerFallback")}</strong> — {`${t("account.chatWith").replace("{name}", otherName || t("account.chatSellerFallback"))}${listingId != null ? ` · #${listingId}` : ""}`}
                            </div>
                        )}
                    </div>
                    <button className="chat-modal-close" onClick={onClose} aria-label={t("account.chatClose")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* 消息列表 + 下拉加载指示器 */}
                <div className="chat-modal-list-wrap" style={{ position: "relative", flex: 1, overflow: "hidden" }}>
                    {/* 顶部拉弹簧指示器（列表下移后自然露出） */}
                    <div
                        className="chat-pull-indicator"
                        style={{
                            position: "absolute",
                            left: 0, right: 0, top: 0,
                            height: PULL_THRESHOLD,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            fontSize: 12,
                            color: "#9CA3AF",
                            opacity: pullDistance > 0 ? 1 : 0,
                            transition: pullState === "idle" ? "opacity 0.2s" : "none",
                            pointerEvents: "none",
                        }}
                    >
                        {pullState === "loading" || conv.loadingMore ? (
                            <>
                                <div className="chat-modal-loading-spinner" />
                                <span>{t("account.loading")}</span>
                            </>
                        ) : pullState === "ready" ? (
                            <>
                                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transform: "rotate(180deg)" }}>
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                                <span>{t("account.chatPullRelease")}</span>
                            </>
                        ) : pullState === "pulling" ? (
                            <>
                                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                                <span>{t("account.chatPullLoadMore")}</span>
                            </>
                        ) : null}
                    </div>

                    <div
                        ref={scrollRef}
                        className="chat-modal-list"
                        onScroll={handleScroll}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                        style={{ position: "absolute", inset: 0, touchAction: "pan-y" }}
                    >
                        {/* 初次加载 */}
                        {conv.loading && conv.list.length === 0 && (
                            <div className="chat-modal-empty-state">
                                <div className="chat-modal-loading-spinner" />
                                <span>{t("account.loading")}</span>
                            </div>
                        )}

                        {/* 空会话 */}
                        {!conv.loading && conv.list.length === 0 && (
                            <div className="chat-modal-empty-state">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32" style={{ opacity: 0.5 }}>
                                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                                </svg>
                                <span>{t("account.chatEmpty")}</span>
                            </div>
                        )}

                        {/* 顶部 loading 行 — 滚到顶延时中(0.5s) 或 加载更多请求中 时显示 */}
                        {(topLoading || conv.loadingMore) && (
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 10,
                                padding: "16px 0 12px",
                                fontSize: 13,
                                color: "#6B7280",
                                fontWeight: 500,
                            }}>
                                <div className="chat-modal-loading-spinner" style={{ width: 18, height: 18, borderWidth: 2.5 }} />
                                <span>{t("account.loading")}</span>
                            </div>
                        )}

                        {/* 没有更多数据 */}
                        {!conv.loadingMore && !conv.loading && !conv.hasMore && conv.list.length > 0 && (
                            <div style={{
                                textAlign: "center",
                                fontSize: 11,
                                color: "#D1D5DB",
                                padding: "12px 0 6px",
                            }}>{t("account.chatNoMore")}</div>
                        )}

                        {/* 消息气泡 —— 最旧在上面，最新在下面（hook 内部已按 oldest→newest 排序） */}
                        {conv.list.map((msg, idx) => {
                            const mine = isMine(msg);
                            const senderAvatar = msg?.senderAvatar || DEFAULT_AVATAR;
                            const senderName = msg?.senderUsername || t("account.otherParty");
                            return (
                                <div key={msg?.id ?? idx} className={`chat-msg-row ${mine ? "mine" : "theirs"}`}>
                                    {!mine && (
                                        <div className="chat-avatar">
                                            <img src={senderAvatar} alt={senderName} />
                                        </div>
                                    )}
                                    <div className="chat-bubble-wrap">
                                        {!mine && (
                                            <div className="chat-bubble-name">{senderName}</div>
                                        )}
                                        <div className={`chat-bubble ${mine ? "mine" : "theirs"}`}>
                                            <div className="chat-bubble-text">{unescape(msg?.content)}</div>
                                            <div className="chat-bubble-time">{fmtTime(msg?.createTime || msg?.time)}</div>
                                        </div>
                                    </div>
                                    {mine && (
                                        <div className="chat-avatar">
                                            <img src={senderAvatar} alt="" />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* 输入区 */}
                <div className="chat-modal-input">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={t("account.chatPlaceholder")}
                        rows={1}
                        maxLength={2000}
                    />
                    <button
                        className="chat-modal-send"
                        onClick={handleSend}
                        disabled={!input.trim() || sendMut.isPending}
                    >
                        {sendMut.isPending ? (
                            <div className="chat-modal-send-spinner" />
                        ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                                <line x1="22" y1="2" x2="11" y2="13" />
                                <polygon points="22 2 15 22 11 13 2 9 22 2" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
